// The athlete's v2 coach: /v2/me, and the live client (src/v2/client.js):
// the calendar and thread as the stream builds them, and the command queue.
// Every athlete is v2.
//
// On sign-in the app also reports the device's time zone (PUT /v2/me/device,
// D42), for every athlete: their "today" and the coach's scheduled passes
// follow it once they are on v2.
//
// A human coach viewing an athlete (CoachingContext) reads GET
// /v2/me/viewing?athlete_id= instead of /v2/me: a v2 athlete is shown
// through the v2 client too, every read carrying athlete_id (viewing.js),
// read-only (`readOnly`: no commands, no composer) and kept in memory only,
// not in this browser's store. A 403 (not their coach) shows no athlete coach.
//
// Until the backend answers, `coachMode` is 'unknown' (or 'v2' from the last
// /v2/me this browser stored for the user, which starts the client at once),
// and a failed read is retried with backoff and on focus/online
// (aithlo-frontend#61, v2/meReader.js).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { v2API } from '../services/api';
import { useAuth } from './AuthContext';
import { useCoaching } from './CoachingContext';
import { createV2Client } from '../v2/client';
import { browserPersist, memoryPersist } from '../v2/persist';
import { v2Mode, viewerAPI } from '../v2/viewing';
import { coachModeOf, createMeReader, loadMe, opensCoachHub, saveMe } from '../v2/meReader';
import { coachingUnread } from '../v2/threadView';
import { useNotifications } from './NotificationSSEContext';

// Exported for the dev previews (/dev/coach), which provide a stand-in client; the app uses V2Provider.
export const V2Context = createContext({ enabled: false, ready: false, readOnly: false, me: null, client: null, api: v2API, confirmAdult: () => {}, coachMode: 'unknown', reconnecting: false, retryMe: () => {} });

const deviceZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
};

export function V2Provider({ children }) {
  const { user } = useAuth();
  const { isViewingAthlete, viewingAthlete } = useCoaching() || {};
  const { setHub } = useNotifications() || {};
  const viewedId = isViewingAthlete ? viewingAthlete?.user_id || null : null;
  const signedIn = Boolean(user);
  const selfId = user?.id ?? user?.user_id ?? null;
  // The fresh read ({ data } once /v2/me or /v2/me/viewing answered, null
  // before or while failing) and the stored /v2/me a cold load starts from.
  const [answer, setAnswer] = useState(null);
  const [cached, setCached] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [client, setClient] = useState(null);
  const readerRef = useRef(null);

  useEffect(() => {
    setAnswer(null);
    setReconnecting(false);
    // Viewing: kept in memory only, so no head start from this browser's store.
    setCached(signedIn && !viewedId ? loadMe(selfId) : null);
    if (!signedIn) return undefined;
    let reported = false;
    const read = async () => {
      if (viewedId) {
        try {
          const { data } = await v2API.viewing(viewedId);
          return data;
        } catch (e) {
          // Not this athlete's coach: an answer (today's coach view), not a failure.
          if (e?.response?.status === 403) return null;
          throw e;
        }
      }
      if (!reported) {
        reported = true;
        const zone = deviceZone();
        if (zone) {
          try { await v2API.reportDevice(zone); } catch { /* the profile country stands in (D42) */ }
        }
      }
      const { data } = await v2API.me();
      return data;
    };
    const reader = createMeReader({
      read,
      onChange: ({ answer: a, failed }) => {
        setReconnecting(failed);
        if (!a) return;
        setAnswer(a);
        if (!viewedId && a.data) saveMe(selfId, a.data);
      },
    });
    readerRef.current = reader;
    reader.start();
    return () => { reader.stop(); if (readerRef.current === reader) readerRef.current = null; };
  }, [signedIn, viewedId, selfId]);

  // A cached /v2/me stands in until the fresh one lands.
  const coachMode = signedIn ? coachModeOf({ answer, cached }) : 'unknown';
  const known = answer ? answer.data : coachMode === 'v2' ? cached : null;
  const mode = v2Mode({
    isViewingAthlete: Boolean(viewedId),
    me: viewedId ? null : known,
    viewing: viewedId ? known : null,
  });
  const { enabled, readOnly, userId, athleteId } = mode;
  const api = useMemo(() => (athleteId ? viewerAPI(v2API, athleteId) : v2API), [athleteId]);

  useEffect(() => {
    if (!userId) { setClient(null); return undefined; }
    const c = createV2Client({ userId, api, readOnly, persist: readOnly ? memoryPersist() : browserPersist() });
    c.start();
    setClient(c);
    return () => c.stop();
  }, [userId, api, readOnly]);

  // 18+ just confirmed (the terms modal, onboarding): nothing asks again before /v2/me is read again.
  const confirmAdult = useCallback(() => {
    setAnswer((a) => (a?.data ? { data: { ...a.data, adult_confirmed: true } } : a));
    setCached((m) => (m ? { ...m, adult_confirmed: true } : m));
  }, []);

  const retryMe = useCallback(() => readerRef.current?.retry(), []);

  // The notification hub is for a coach viewing an athlete only
  // (meReader.opensCoachHub): everyone's own badge comes from /v2/me and the
  // v2 stream (aithlo-frontend#67).
  const hub = Boolean(signedIn) && opensCoachHub({ viewing: Boolean(viewedId) });
  useEffect(() => { setHub?.(hub); }, [setHub, hub]);

  // `ready`: which app this athlete gets is known (a v2 athlete's client is running).
  const ready = coachMode !== 'unknown' && (!enabled || Boolean(client));
  const value = useMemo(() => ({
    enabled: enabled && Boolean(client), ready, readOnly, me: mode.me, client, api, confirmAdult,
    coachMode, reconnecting, retryMe, hub,
  }), [enabled, ready, readOnly, mode.me, client, api, confirmAdult, coachMode, reconnecting, retryMe, hub]);
  return <V2Context.Provider value={value}>{children}</V2Context.Provider>;
}

/**
 * { enabled, ready, readOnly, me, client, api, confirmAdult, coachMode,
 * reconnecting, retryMe }: `enabled` only for a v2 athlete whose client is running
 * (their own, or one a coach is viewing: then `readOnly`); `ready` once that
 * is known; `api` is v2API, with athlete_id on every read while viewing.
 * `coachMode` is 'v2' (answered, or cached), 'none' (viewing someone not
 * theirs) or 'unknown' (not read yet, or failing: `reconnecting`).
 */
export const useV2 = () => useContext(V2Context);

const EMPTY = { status: 'off', cal: null, pending: [], failed: [], outcomes: {}, live: {} };
const noop = () => () => {};

/** The client's state, re-rendering on every change (EMPTY until the client is running). */
export function useV2State() {
  const { client } = useV2();
  return useSyncExternalStore(client ? client.subscribe : noop, client ? client.getSnapshot : () => EMPTY);
}

/**
 * The Coaching tab's badge: the hub's count while a coach views an athlete,
 * else v2's: /v2/me's coaching_unread
 * kept fresh by the stream's coaching.unread (threadView.coachingUnread).
 */
export function useCoachingBadge() {
  const { hub, me } = useV2();
  const { cal } = useV2State();
  const { unreadCount = 0 } = useCoaching() || {};
  return hub ? unreadCount : coachingUnread(cal, me);
}
